// src/pages/admin/layers/LayerForm.tsx

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../../config/firebaseConfig";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { ArrowDownOnSquareIcon, ChevronLeftIcon } from "@heroicons/react/20/solid";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Dialog } from "@headlessui/react";

const normNo = (s: string) => s.trim().toUpperCase();
const normTitle = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

export default function LayerForm() {
  const { id } = useParams(); // edit if present
  const navigate = useNavigate();
  const { user } = useSelector((s: RootState) => s.auth);

  const [layerNo, setLayerNo] = useState("");
  const [title, setTitle] = useState("");
  const [short, setShort] = useState("");
  const [content, setContent] = useState("");

  const [initial, setInitial] = useState<any>(null);

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);

  const [layerNoErr, setLayerNoErr] = useState<string | null>(null);
  const [titleErr, setTitleErr] = useState<string | null>(null);

  const [discardOpen, setDiscardOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEdit = !!id;
  const canSave = !!layerNo.trim() && !!title.trim() && !!content.trim() && !saving;

  // detect unsaved changes
  const isDirty =
    initial &&
    (initial.layerNo !== layerNo ||
      initial.title !== title ||
      initial.short !== short ||
      initial.content !== content);

  // load existing when editing
  useEffect(() => {
    if (!id) {
      setInitial({ layerNo, title, short, content });
      return;
    }

    (async () => {
      const ref = doc(db, "layers", "meta", "data", id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const d = snap.data();
        const loaded = {
          layerNo: d.layerNo || "",
          title: d.title || "",
          short: d.short || "",
          content: d.content || "",
        };
        setLayerNo(loaded.layerNo);
        setTitle(loaded.title);
        setShort(loaded.short);
        setContent(loaded.content);
        setInitial(loaded);
      }
      setLoading(false);
    })();
  }, [id]);

  const handleBackOrCancel = () => {
    if (isDirty) setDiscardOpen(true);
    else navigate("/admin/layers");
  };

  const save = async () => {
    const noKey = normNo(layerNo);
    const titleKey = normTitle(title);

    if (!/^L\d{2,3}$/.test(noKey)) {
      setLayerNoErr("Layer number must look like L00 or L001 (max 3 digits).");
      return;
    }
    if (title.trim().length < 3 || /^\d/.test(title)) {
      setTitleErr("Title must be ≥ 3 characters and not start with a number.");
      return;
    }
    setLayerNoErr(null);
    setTitleErr(null);

    setSaving(true);
    try {
      const metaRef = doc(db, "layers", "meta");
      const dataCol = collection(metaRef, "data");

      await runTransaction(db, async (tx) => {
        const layerRef = id ? doc(dataCol, id) : doc(dataCol);

        const noIndexRef = doc(collection(metaRef, "layerNumberIndex"), noKey);
        const titleIndexRef = doc(collection(metaRef, "layerTitleIndex"), titleKey);

        if (!id) {
          if ((await tx.get(noIndexRef)).exists()) throw new Error("DUP_NO");
          if ((await tx.get(titleIndexRef)).exists()) throw new Error("DUP_TITLE");

          tx.set(
            layerRef,
            {
              layerNo: layerNo.trim(),
              layerNoKey: noKey,
              title: title.trim(),
              titleKey,
              short: short.trim(),
              content,
              createdBy: user?.uid ?? null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          tx.set(noIndexRef, { layerId: layerRef.id, createdAt: serverTimestamp() });
          tx.set(titleIndexRef, { layerId: layerRef.id, createdAt: serverTimestamp() });
          return;
        }

        const oldSnap = await tx.get(layerRef);
        if (!oldSnap.exists()) throw new Error("MISSING");
        const old = oldSnap.data() || {};
        const oldContent: string = old.content ?? "";
        const contentChanged = oldContent !== content;

        const noHit = await tx.get(noIndexRef);
        if (noHit.exists() && noHit.data().layerId !== id) throw new Error("DUP_NO");
        const titleHit = await tx.get(titleIndexRef);
        if (titleHit.exists() && titleHit.data().layerId !== id) throw new Error("DUP_TITLE");

        if (old.titleKey && old.titleKey !== titleKey) {
          const oldTitleIndex = doc(collection(metaRef, "layerTitleIndex"), old.titleKey);
          tx.delete(oldTitleIndex);
          tx.set(titleIndexRef, { layerId: id, createdAt: serverTimestamp() });
        }

        if (contentChanged) {
          const prevCreatedAt = old.createdAt ?? serverTimestamp();
          const verRef = doc(collection(layerRef, "versions"));
          tx.set(verRef, {
            content: oldContent,
            title: old.title ?? "",
            short: old.short ?? "",
            createdAt: prevCreatedAt,
            createdBy: old.createdBy ?? user?.uid ?? null,
          });
        }

        tx.set(
          layerRef,
          {
            layerNo: layerNo.trim(),
            layerNoKey: noKey,
            title: title.trim(),
            titleKey,
            short: short.trim(),
            content,
            createdBy: old.createdBy ?? user?.uid ?? null,
            ...(contentChanged ? { createdAt: serverTimestamp() } : {}),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      if (id) {
        const vq = query(
          collection(db, "layers", "meta", "data", id, "versions"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(vq);
        const extra = snap.docs.slice(1);
        await Promise.all(extra.map((d) => deleteDoc(d.ref)));
      }

      navigate("/admin/layers");
    } catch (e: any) {
      if (e.message === "DUP_NO") setLayerNoErr("This layer number already exists.");
      else if (e.message === "DUP_TITLE") setTitleErr("This layer title already exists.");
      else setTitleErr("Failed to save layer.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="mx-8 my-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={handleBackOrCancel}
          className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Layers
        </button>

        <div className="flex gap-2">
          <button
            onClick={handleBackOrCancel}
            className="rounded-lg border border-zinc-300 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-600/90 disabled:opacity-50"
          >
            <ArrowDownOnSquareIcon className="h-4 w-4" />
            {isEdit ? "Update" : "Save"}
          </button>
        </div>
      </div>

      {/* Form */}
      <h2 className="mb-6 text-xl font-semibold text-zinc-800">
        {isEdit ? "Edit layer" : "Create layer"}
      </h2>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-zinc-700">
            Layer number <span className="text-rose-600">*</span>
          </label>
          <input
            value={layerNo}
            onChange={(e) => setLayerNo(e.target.value)}
            disabled={isEdit}
            className={`field-grey ${isEdit ? "field-disabled" : ""}`}
          />
          {layerNoErr && <p className="mt-1 text-xs text-rose-600">{layerNoErr}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-zinc-700">
            Layer title <span className="text-rose-600">*</span>
          </label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="field-grey"
          />
          {titleErr && <p className="mt-1 text-xs text-rose-600">{titleErr}</p>}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-zinc-700">Short description</label>
          <input
            value={short}
            onChange={(e) => setShort(e.target.value)}
            className="field-grey"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-zinc-700">Content</label>
          <textarea
            rows={13}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="field-grey"
          />
        </div>
      </div>

      {/* -------------------------------------------- */}
      {/* DISCARD CHANGES DIALOG */}
      {/* -------------------------------------------- */}
      <Dialog open={discardOpen} onClose={() => setDiscardOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold text-zinc-800">
              Discard all changes?
            </Dialog.Title>

            <p className="mt-3 text-sm text-zinc-600">
              Are you sure you want to discard all the changes?
            </p>

            <div className="mt-6 flex justify-end gap-3">
              <button
                disabled={isSubmitting}
                onClick={() => setDiscardOpen(false)}
                className="rounded-md bg-zinc-200 px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-300 disabled:opacity-50"
              >
                No, go back
              </button>

              <button
                disabled={isSubmitting}
                onClick={() => {
                  setIsSubmitting(true);
                  navigate("/admin/layers");
                }}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm text-white hover:bg-brand-700 disabled:opacity-50"
              >
                Yes, discard
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
